import React, { useState, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Role } from '../../hooks/useFetchRoles';
import { API } from '@/utils/API';

interface RoleAssignmentData {
  id: string;
  user_id: string;
  role_key: string;
  resource_type?: string;
  resource_id?: string;
  user: {
    id: string;
    email_address: string;
  };
  resource?: {
    id: string;
    name: string;
  } | null;
}

interface EditRoleAssignmentFormProps {
  roles: Record<string, Role>;
  roleAssignment: RoleAssignmentData;
}

// Helper function to add spaces between capital letters
const addSpacesBetweenCapitals = (text: string): string => {
  return text.replace(/([a-z])([A-Z])/g, '$1 $2');
};

// Create schema factory function to handle dynamic validation
const createRoleAssignmentSchema = () =>
  z.object({
    role_key: z.string().min(1, 'Role is required'),
  });

type RoleAssignmentSchema = z.infer<
  ReturnType<typeof createRoleAssignmentSchema>
>;

const EditRoleAssignmentForm: React.FC<EditRoleAssignmentFormProps> = ({
  roles,
  roleAssignment,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  const [submissionSuccess, setSubmissionSuccess] = useState('');

  const schema = useMemo(() => createRoleAssignmentSchema(), []);
  const form = useForm<RoleAssignmentSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      role_key: roleAssignment.role_key,
    },
    mode: 'onChange',
  });

  const {
    formState: { errors },
  } = form;

  const onSubmit = async (data: RoleAssignmentSchema) => {
    setIsSubmitting(true);
    setSubmissionError('');
    setSubmissionSuccess('');

    try {
      const response = await API.authenticated.admin.fetch(
        `/role_assignments/${roleAssignment.id}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            role_assignment: {
              role_key: data.role_key,
            },
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || 'Failed to update role assignment'
        );
      }

      setSubmissionSuccess('Role assignment updated successfully');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'An unexpected error occurred';
      setSubmissionError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <div className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium text-gray-700">
            Email
          </Label>
          <Input
            id="email"
            type="email"
            value={roleAssignment.user.email_address}
            className="w-full"
            disabled={true}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="role" className="text-sm font-medium text-gray-700">
            Role
          </Label>
          <Controller
            name="role_key"
            control={form.control}
            render={({ field }) => {
              return (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(roles).map(role => (
                      <SelectItem key={role} value={role}>
                        {roles[role].name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              );
            }}
          />
          {errors.role_key && (
            <p className="text-sm text-red-600 mt-1">
              {errors.role_key.message}
            </p>
          )}
        </div>

        {roleAssignment.resource_type && (
          <div className="space-y-2">
            <Label
              htmlFor="resource-type"
              className="text-sm font-medium text-gray-700"
            >
              Resource Type
            </Label>
            <Input
              id="resource-type"
              value={addSpacesBetweenCapitals(roleAssignment.resource_type)}
              className="w-full"
              disabled={true}
            />
          </div>
        )}

        {roleAssignment.resource && (
          <div className="space-y-2">
            <Label
              htmlFor="resource"
              className="text-sm font-medium text-gray-700"
            >
              {addSpacesBetweenCapitals(
                roleAssignment.resource_type || 'Resource'
              )}
            </Label>
            <Input
              id="resource"
              value={roleAssignment.resource.name}
              className="w-full"
              disabled={true}
            />
          </div>
        )}

        <div className="flex gap-4 pt-4">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() =>
              (window.location.href = `/admin/users/${roleAssignment.user_id}`)
            }
            disabled={isSubmitting}
          >
            Back
          </Button>
          <Button
            type="button"
            className="flex-1"
            onClick={form.handleSubmit(onSubmit)}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Updating...' : 'Update Role Assignment'}
          </Button>
        </div>

        {submissionSuccess && (
          <p className="text-green-500 text-center">{submissionSuccess}</p>
        )}
        {submissionError && (
          <p className="text-red-500 text-center">{submissionError}</p>
        )}
      </div>
    </Form>
  );
};

export default EditRoleAssignmentForm;
